<template>
  <div v-loading="isLoading">
    <el-form
      :model="form"
      :rules="rules"
      label-width="170px"
    >
      <el-form-item
        v-if="!studyId"
        :label="$t('search_engines')"
        prop="searchEngines"
      >
        <search-engine-selector
          v-model="form.searchEngines"
          :search-engines="availableSearchEngines"
        >
        </search-engine-selector>
      </el-form-item>
      <el-form-item :label="$t('url')" prop="url" :rules="urlRules">
        <el-input
          v-model="form.url"
          :placeholder="$t('url_placeholder')"
        ></el-input>
      </el-form-item>
      <el-form-item :label="$t('custom_name')">
        <el-input v-model="form.customName"></el-input>
      </el-form-item>
      <el-form-item v-if="currentUser.is_admin" :label="$t('study_type')">
        <el-select
          v-if="studyTypes"
          v-model="form.studyType"
        >
          <el-option
            v-for="(studyType, index) in studyTypes"
            :key="index"
            :label="$t(studyType)"
            :value="studyType"
          >
        </el-option>
      </el-select>
    </el-form-item>
     <el-tooltip :content="$t('shopping_source_tooltip')" placement="bottom">
        <el-form-item :label="$t('shopping_source')" prop="shoppingSource">
          <el-input v-model="form.shoppingSource" :placeholder="form.shoppingPlaceholder"></el-input>
        </el-form-item>
      </el-tooltip>
    </el-form>
    <div class="text-right">
      <el-button
        plain
        @click="closeDialog"
        >{{ $t("cancel") }}</el-button
      >
      <el-button
        plain
        type="primary"
        :disabled="!isFormSubmitable"
        @click="submitForm"
        >{{ $t("confirm") }}</el-button
      >
    </div>
  </div>
</template>

<script>
import { availableSearchEnginesMixin } from "@/mixins/availableSearchEnginesMixin";
import SearchEngineSelector from "@/components/formItems/SearchEngineSelector";
import { RepositoryFactory } from "@/services/repositoryFactory";
import UrlFormatter from "@/javascripts/urlFormatter";

export default {
  mixins: [availableSearchEnginesMixin],
  components: {
    SearchEngineSelector,
  },
  props: {
    studyId: Number,
    studyTypes: Array,
    currentUser: Object,
  },
  data() {
    return {
      form: {
        searchEngines: [],
        url: "",
        customName: "",
        studyType: "secondary_study",
        shoppingSource: "",
        originalRootDomain: ""
      },
      isLoading: false,
      rules: {
        searchEngines: [
          {
            required: true,
            message: this.$t("searchEngines_mandatory"),
            trigger: "change",
          },
        ],
        url: [
          {
            required: true,
            message: this.$t("url_mandatory"),
            trigger: "change",
          }
        ],
        shoppingSource: [
          {
            required: true,
            message: this.$t("shoppingSource_mandatory"),
            trigger: "change",
          }
        ]
      },
    };
  },
  computed: {
    defaultSearchEngine() {
      if (this.form.searchEngines.length === 0) {
        return null;
      } else if (this.form.searchEngines.length === 1) {
        return this.form.searchEngines[0];
      } else {
        return this.form.searchEngines.sort((a, b) => {
          if (a.device === b.device) {
            return 0;
          } else if (a.device === "DESKTOP" && b.device === "MOBILE") {
            return -1;
          } else {
            return 1;
          }
        })[0];
      }
    },
    isFormSubmitable() {
     if (this.studyId && this.originalRootDomain) {
        const r1 = this.originalRootDomain;
        const r2 = UrlFormatter.domain(this.form.url);
        if (r1 !== r2) return false;
      }
      if (this.form.searchEngines.length > 0
        && this.form?.shoppingSource?.trim()
        && this.form?.url?.trim()) {
        return true;
      }
      return false;
    },
    urlRules() {
      const rules = [
        { required: true, message: this.$t("url_mandatory"), trigger: 'blur' }
      ];

      if (this.studyId) {
        rules.push({
          validator: (rule, value, callback) => {
            const newRoot = UrlFormatter.domain(value);

            if (this.originalRootDomain !== newRoot) {
              callback(new Error(this.$t("url_domain_error", { domain: this.originalRootDomain })));
            } else {
              callback();
            }
          },
          trigger: 'blur'
        });
      }

      return rules;
    }
  },
  methods: {
    submitForm() {
      this.closeDialog();
      const country = this.form.searchEngines.map(
        (searchEngine) =>
          searchEngine.isoLanguageCode.toLowerCase() +
          "-" +
          searchEngine.isoCountryCode.toLowerCase()
      )[0];
      this.$emit("submit", {
        ...this.form,
        country,
        defaultSearchEngine: this.defaultSearchEngine,
      });
      if (!this.studyId) {
        this.resetForm();
      }
    },
    closeDialog() {
      this.$emit("close-dialog");
    },
    resetForm() {
      this.form.url = "";
      this.form.customName = "";
      this.form.searchEngines = [];
      this.form.studyType = "secondary_study";
    },
    loadStudy() {
      this.isLoading = true;
      this.$api
        .get("/studies/" + this.studyId)
        .then((response) => {
          const data = response.data.data;
          this.form.url = data.url;
          this.originalRootDomain = UrlFormatter.domain(data.url);
          this.form.studyType = data.studyType;
          if (data.name) {
            this.form.customName = data.name;
          }
          if (data.searchEngines) {
            this.form.searchEngines = data.searchEngines;
          }
          this.isLoading = false;
        })
        .catch((error) => {
          this.isLoading = false;
          console.error(error);
        });
    },
  },
  created() {
    if (this.studyId) this.loadStudy();
  },
  watch: {
    studyId() {
      if (this.studyId) this.loadStudy();
    },
  },
};
</script>

<style scoped lang="scss">
.text-right {
  text-align: right;
}
</style>

<i18n>
{
  "en": {
    "url": "URL",
    "url_placeholder": "Example: www.mywebsite.com",
    "custom_name": "Custom name",
    "cancel": "Cancel",
    "confirm": "Confirm",
    "search_engines": "Search engines",
    "main_study": "Main study",
    "study_type": "Study type",
    "secondary_study": "Secondary study",
    "oneshot_study": "Oneshot study",
    "shopping_source_tooltip": "Used to correctly identify the brand in Shopping results and by LLMs.",
    "searchEngines_mandatory": "Please select atleast one search engine",
    "url_mandatory": "Please fill in an url",
    "url_domain_error": "You can only change the subdomain. Root domain must remain as: {domain}",
    "shoppingSource_mandatory": "Please fill in a brand name"
  },
  "fr": {
    "url": "URL",
    "url_placeholder": "Exemple: www.monsite.com",
    "custom_name": "Nom personnalisé",
    "cancel": "Annuler",
    "confirm": "Confirmer",
    "search_engines": "Moteurs de recherche",
    "main_study": "Etude principale",
    "study_type": "Type d'étude",
    "secondary_study": "Etude secondaire",
    "oneshot_study": "Etude oneshot",
    "shopping_source": "Nom de marque",
    "shopping_source_tooltip": "Sert à identifier la marque dans les résultats Shopping et par les LLMs.",
    "searchEngines_mandatory": "Veuillez Sélectionner au moins un moteur de recherche",
    "url_mandatory": "Veuillez saisir une url",
    "url_domain_error": "Merci de ne modifier que le sous-domaine afin de garder ce nom de domaine: {domain}",
    "shoppingSource_mandatory": "Veuillez saisir un nom de marque"
  },
  "de": {
    "url": "URL",
    "url_placeholder": "Beispiel: www.mysite.com",
    "custom_name": "benutzerdefinierter Name",
    "cancel": "Stornieren",
    "confirm": "Bestätigen",
    "search_engines": "Suchmaschine",
    "main_study": "Hauptstudium",
    "study_type": "Studientyp",
    "secondary_study": "Zweitstudium",
    "oneshot_study": "Oneshot-Studie",
    "shopping_source": "Markenname",
    "shopping_source_tooltip": "Dient dazu, die Marke in Shopping-Ergebnissen und von LLMs korrekt zu erkennen.",
    "searchEngines_mandatory": "Bitte wählen Sie mindestens eine Suchmaschine aus",
    "url_mandatory": "Bitte geben Sie eine URL ein",
    "url_domain_error": "Sie können nur die Subdomain ändern. Die Root-Domain muss wie folgt bleiben: {domain}",
    "shoppingSource_mandatory": "Bitte geben Sie einen Markennamen ein"
  }
}
</i18n>
